import { ApiError, type AdminCapability } from "@studyward/shared";
import { buttonClass, FullPageLoading, Skeleton } from "@studyward/ui";
import { lazy, Suspense, type ReactNode } from "react";
import { Link, Route, Routes } from "react-router";
import { Shell } from "./components/Shell";
import { AuthFrame, errorText, PageHeader } from "./components/ui";
import { isConfigured } from "./lib/env";
import { MeProvider, useAuth, useCan, useMe } from "./lib/session";
import { MfaStep, NoAccess, PasswordStep } from "./pages/SignIn";

// Each view loads when it is first opened, so sign-in and the dashboard stay small.
const AuditLog = lazy(() => import("./pages/AuditLog").then((m) => ({ default: m.AuditLog })));
const Content = lazy(() => import("./pages/Content").then((m) => ({ default: m.Content })));
const Dashboard = lazy(() => import("./pages/Dashboard").then((m) => ({ default: m.Dashboard })));
const Jobs = lazy(() => import("./pages/Jobs").then((m) => ({ default: m.Jobs })));
const Moderation = lazy(() => import("./pages/Moderation").then((m) => ({ default: m.Moderation })));
const Pricing = lazy(() => import("./pages/Pricing").then((m) => ({ default: m.Pricing })));
const Programmes = lazy(() => import("./pages/Programmes").then((m) => ({ default: m.Programmes })));
const Revenue = lazy(() => import("./pages/Revenue").then((m) => ({ default: m.Revenue })));
const Support = lazy(() => import("./pages/Support").then((m) => ({ default: m.Support })));
const SettingsPage = lazy(() => import("./pages/Settings").then((m) => ({ default: m.SettingsPage })));
const UsersPage = lazy(() => import("./pages/Users").then((m) => ({ default: m.UsersPage })));

/** A view the role can't open is absent from its sidebar and denied by URL (§10.3); the API answers 403 regardless. */
function Guard({ capability, children }: { capability: AdminCapability; children: ReactNode }) {
  if (!useCan(capability)) {
    return (
      <>
        <PageHeader title="Not available for your role" subtitle="Your admin role can't open this view. Ask an owner if you need it." />
        <Link to="/" className={buttonClass("secondary")}>
          Back to the dashboard
        </Link>
      </>
    );
  }
  return <>{children}</>;
}

function NotFound() {
  return (
    <>
      <PageHeader title="Page not found" subtitle="That address isn't part of the admin console." />
      <Link to="/" className={buttonClass("primary")}>
        Go to the dashboard
      </Link>
    </>
  );
}

function Console() {
  const me = useMe();
  if (me.isPending) return <FullPageLoading label="Checking your admin access" />;
  if (me.isError) {
    const err = me.error;
    if (err instanceof ApiError && err.code === "forbidden") return <NoAccess message={err.message} />;
    return (
      <AuthFrame title="The console didn't load" subtitle={errorText(err)}>
        <button type="button" className={buttonClass("primary")} onClick={() => void me.refetch()}>
          Try again
        </button>
      </AuthFrame>
    );
  }
  return (
    <MeProvider value={me.data}>
      <Shell>
        <Suspense fallback={<Skeleton className="h-96 w-full" label="Loading" />}>
        <Routes>
          <Route path="/" element={<Guard capability="dashboard"><Dashboard /></Guard>} />
          <Route path="/users" element={<Guard capability="users.view"><UsersPage /></Guard>} />
          <Route path="/moderation" element={<Guard capability="moderation"><Moderation /></Guard>} />
          <Route path="/jobs" element={<Guard capability="jobs.view"><Jobs /></Guard>} />
          <Route path="/content" element={<Guard capability="content"><Content /></Guard>} />
          <Route path="/revenue" element={<Guard capability="revenue.view"><Revenue /></Guard>} />
          <Route path="/pricing" element={<Guard capability="pricing.view"><Pricing /></Guard>} />
          <Route path="/programmes" element={<Guard capability="programmes"><Programmes /></Guard>} />
          <Route path="/support" element={<Guard capability="support"><Support /></Guard>} />
          <Route path="/support/:ticketId" element={<Guard capability="support"><Support /></Guard>} />
          <Route path="/settings" element={<Guard capability="settings"><SettingsPage /></Guard>} />
          <Route path="/audit-log" element={<Guard capability="audit_log"><AuditLog /></Guard>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </Shell>
    </MeProvider>
  );
}

export function App() {
  const { state } = useAuth();
  if (!isConfigured) {
    return (
      <AuthFrame title="Admin console isn't configured" subtitle="Set SUPABASE_URL and SUPABASE_ANON_KEY in the environment and rebuild.">
        <span />
      </AuthFrame>
    );
  }
  if (state.status === "loading") return <FullPageLoading label="Loading the admin console" />;
  if (state.status === "signed_out") return <PasswordStep />;
  if (state.status === "needs_mfa") return <MfaStep />;
  return <Console />;
}
